import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./IN1888.module.css";
import api from "../../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IN1888EnviadosOverlay from "../../components/IN1888EnviadosOverlay";
import EnviadosModule from "./EnviadosModule";
import { IN1888_COLUMNS, normalizeCompra, normalizeVenda } from "./utils/in1888Columns";
import { buildTxtFromLines, downloadTxtFile } from "./utils/txtExport";
import { buildIn1888Line, inferCpfFields } from "./utils/in1888TxtLine";

const mapVendaToRow = (item = {}) => ({
  id: item.ID ?? item.id ?? item.NF ?? Math.random().toString(36).slice(2),
  compra: "Venda",
  data: item.DATA ?? item.data ?? "",
  nomeBrl: item.NOMEBRL ?? item.nomeBrl ?? "—",
  nomeBob: item.NOMEBOB ?? item.nomeBob ?? "—",
  valorBrl: item.VALORBRL ?? item.valorBrl ?? 0,
  valorBob: item.VALORBOB ?? item.valorBob ?? 0,
  exchange: "P2P",
  usdtQuant: item.USDT_QUANT ?? item.usdtQuant ?? null,
  usdtPreco: item.USDT_PRECO_UN ?? item.usdtPreco ?? null,
  spreadPct: item.SPREAD_PCT ?? item.spreadPct ?? null,
  usdtPriceSrc: item.USDT_PRICE_SRC ?? item.usdtPriceSrc ?? null,
  cpf: item.CPF ?? "",
});

const mapCompraToRow = (item = {}) => ({
  id: item.ID ?? item.id ?? item.ORDER_NUMBER ?? Math.random().toString(36).slice(2),
  compra: "Compra",
  data: item.DATA ?? item.data ?? "",
  nomeBrl: item.NOMEBRL ?? item.nomeBrl ?? "—",
  nomeBob: item.NOMEBOB ?? item.nomeBob ?? "—",
  valorBrl: item.VALORBRL ?? item.valorBrl ?? 0,
  valorBob: item.VALORBOB ?? item.valorBob ?? 0,
  exchange: item.EXCHANGE ?? item.exchange ?? "—",
  usdtQuant: item.USDT_QUANT ?? null,
  usdtPreco: item.USDT_PRECO_UN ?? null,
  cpf: item.CPF ?? item.cpf ?? "",
});

const parseRowDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}[/-]\d{2}[/-]\d{2}/.test(raw)) {
    const [year, month, day] = raw.split("T")[0].split(/[/-]/);
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  if (/^\d{2}[/-]\d{2}[/-]\d{4}/.test(raw)) {
    const [day, month, year] = raw.split("T")[0].split(/[/-]/);
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const normalized = new Date(raw);
  return Number.isNaN(normalized.getTime()) ? null : normalized;
};

const formatDateForDisplay = (value) => {
  const date = parseRowDate(value);
  if (!date) return value || "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateForExport = (value) => {
  const date = parseRowDate(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

const filterRowsByMonth = (rows, monthFilter) => {
  if (!monthFilter) return rows;
  return rows.filter((row) => {
    const date = parseRowDate(row.data);
    if (!date) return false;
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}` === monthFilter;
  });
};

const formatCurrencyBRL = (value = 0) => {
  const resolved = Number(value) || 0;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(resolved);
};

const formatNumberComma = (value = 0, decimals = 2) => {
  const resolved = Number(value) || 0;
  return resolved.toFixed(decimals).replace(".", ",");
};

const formatBOB = (value) => `${formatNumberComma(value, 2)} Bs`;

const formatUSDTQuant = (value) => `${formatNumberComma(value, 8)}`;

const normalizeName = (value) => {
  if (!value) return "";
  return String(value).replace(/\s+/g, " ").trim().toUpperCase();
};

const normalizeKey = (value) => {
  if (!value) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
};

const isExchangeName = (nome) => {
  const key = normalizeKey(nome);
  if (!key) return false;
  const compact = key.replace(/[^A-Z0-9]/g, "");
  const tokens = [
    "BINANCE",
    "BYBIT",
    "COINBASE",
    "KUCOIN",
    "KUKOIN",
    "GATE",
    "GATEIO",
    "OKX",
    "HUOBI",
    "BITGET",
    "MEXC",
    "PAXFUL",
    "LOCALBITCOINS",
    "ELDORADO",
  ];
  return tokens.some((token) => key.includes(token) || compact.includes(token));
};

const pickExportName = (nomeBrl, nomeBob) => {
  const hasNomeBob = Boolean(nomeBob && String(nomeBob).trim());
  if (isExchangeName(nomeBrl) && hasNomeBob) {
    return nomeBob;
  }
  if (!nomeBrl || !String(nomeBrl).trim()) {
    return nomeBob || "";
  }
  return nomeBrl;
};

const calculateTotals = (rows) =>
  rows.reduce(
    (acc, row) => ({
      valorBrl: acc.valorBrl + Number(row.valorBrl || 0),
      valorBob: acc.valorBob + Number(row.valorBob || 0),
      usdtQuant: acc.usdtQuant + Number(row.usdtQuant || 0),
    }),
    { valorBrl: 0, valorBob: 0, usdtQuant: 0 }
  );

const buildSaleLine = (row) => {
  const date = formatDateForExport(row.data);
  const valorBrlNum = Number(row.valorBrl);
  const quantNum = Number(row.usdtQuant);
  if (!date || !(valorBrlNum > 0) || !(quantNum > 0)) return null;

  const valorBrl = formatNumberComma(valorBrlNum, 2);
  const quant = formatNumberComma(quantNum, 8);
  const { indicadorCpf, pais, cpf } = inferCpfFields(row.cpf);
  const nome = normalizeName(pickExportName(row.nomeBrl, row.nomeBob));

  return buildIn1888Line("0120", [
    date,
    "I",
    valorBrl,
    "0,00",
    "USDT",
    quant,
    indicadorCpf,
    pais,
    cpf,
    "",
    nome,
  ]);
};

const buildCompraLine = (row) => {
  const date = formatDateForExport(row.data);
  const valorBrlNum = Number(row.valorBrl);
  const quantNum = Number(row.usdtQuant);
  if (!date || !(valorBrlNum > 0) || !(quantNum > 0)) return null;

  const valorBrl = formatNumberComma(valorBrlNum, 2);
  const quant = formatNumberComma(quantNum, 8);
  const nome = normalizeName(pickExportName(row.nomeBrl, row.nomeBob));

  const { indicadorCpf, pais, cpf } = inferCpfFields(row.cpf);
  return buildIn1888Line("0110", [
    date,
    "I",
    valorBrl,
    "0,00",
    "USDT",
    quant,
    indicadorCpf,
    pais,
    cpf,
    "",
    nome,
  ]);
};

const getFileName = (baseName, month) =>
  month ? `${baseName}_${month}.txt` : `${baseName}.txt`;

const exportFile = (rows, builder, filename) => {
  if (!rows.length) {
    return;
  }
  const invalidRows = rows.filter((row) => {
    const hasDate = Boolean(formatDateForExport(row.data));
    const valorBrlOk = Number(row.valorBrl) > 0;
    const usdtQuantOk = Number(row.usdtQuant) > 0;
    return !(hasDate && valorBrlOk && usdtQuantOk);
  });

  if (invalidRows.length > 0) {
    toast.error(
      `Existem ${invalidRows.length} registros sem USDT calculado. Rode o backfill antes de exportar.`
    );
    return;
  }

  const lines = rows.map(builder).filter((line) => line);
  const payload = buildTxtFromLines(lines);
  downloadTxtFile(filename, payload);
};

const getRangeParams = (monthFilter) => {
  if (!monthFilter) return null;
  const [year, month] = monthFilter.split("-");
  if (!year || !month) return null;
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  return {
    dataInicio: `${year}-${month}-01`,
    dataFim: `${year}-${month}-${String(lastDay).padStart(2, "0")}`,
  };
};

export default function IN1888() {
  const [vendas, setVendas] = useState([]);
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [dataSource, setDataSource] = useState("LEGACY");
  const [sentItems, setSentItems] = useState([]);
  const [sentCompras, setSentCompras] = useState([]);
  const [sentVendas, setSentVendas] = useState([]);
  const [openSent, setOpenSent] = useState(false);
  const notifiedMissingUsdtRef = useRef(false);
  const filteredVendas = useMemo(
    () => filterRowsByMonth(vendas, filterMonth),
    [vendas, filterMonth]
  );
  const filteredCompras = useMemo(
    () => filterRowsByMonth(compras, filterMonth),
    [compras, filterMonth]
  );
  const comprasCanon = useMemo(
    () => filteredCompras.map(normalizeCompra),
    [filteredCompras]
  );
  const vendasCanon = useMemo(
    () => filteredVendas.map(normalizeVenda),
    [filteredVendas]
  );
  useEffect(() => {
    let active = true;
    const range = getRangeParams(filterMonth);

    const loadLegacy = async () => {
      const requestParams = range ? { params: range } : undefined;
      const [responseVendas, responseCompras] = await Promise.all([
        api.get("/vendas", requestParams),
        api.get("/compras", requestParams),
      ]);
      const mappedVendas = (responseVendas?.data ?? []).map(mapVendaToRow);
      const mappedCompras = (responseCompras?.data ?? []).map(mapCompraToRow);
      setVendas(mappedVendas);
      setCompras(mappedCompras);
      setDataSource("LEGACY");
      setSentItems([]);
      setSentCompras([]);
      setSentVendas([]);
      const hasMissingUsdtSrc = mappedVendas.some((row) => !row.usdtPriceSrc);
      if (hasMissingUsdtSrc && !notifiedMissingUsdtRef.current) {
        toast.info("Existem vendas antigas sem USDT calculado. Rode o backfill em /admin/vendas/backfill-usdt.");
        notifiedMissingUsdtRef.current = true;
      }
    };

    const loadIn1888Tables = async () => {
      if (!active) return;
      setLoading(true);
      setErrorMessage("");
      try {
        if (filterMonth) {
          const sentRes = await api.get("/in1888/sent/items", {
            params: { mesRef: filterMonth },
          });
          const items = Array.isArray(sentRes?.data) ? sentRes.data : [];
          if (items.length > 0) {
            const comprasSent = items.filter((item) => String(item.REG || "").trim() === "0110");
            const vendasSent = items.filter((item) => String(item.REG || "").trim() === "0120");
            setSentItems(items);
            setSentCompras(comprasSent);
            setSentVendas(vendasSent);
            setDataSource("SENT");
            setLoading(false);
            return;
          }
        }

        await loadLegacy();
      } catch (error) {
        if (!active) return;
        setErrorMessage("Não foi possível carregar os dados. Tente novamente.");
        console.error("Erro ao carregar dados IN 1888:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadIn1888Tables();
    return () => {
      active = false;
    };
  }, [filterMonth]);
  const totalsVendas = useMemo(
    () => calculateTotals(filteredVendas),
    [filteredVendas]
  );
  const totalsCompras = useMemo(
    () => calculateTotals(filteredCompras),
    [filteredCompras]
  );

  const renderCell = (value) => {
    const label =
      value === null || value === undefined ? "—" : String(value);
    return (
      <span className={styles.cellText} title={label}>
        {label}
      </span>
    );
  };

  const formatUsdt8 = (value) =>
    Number(value || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8,
    });

  const formatPrice4 = (value) =>
    Number(value || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });

  const sentTotals = (rows) =>
    rows.reduce(
      (acc, row) => ({
        quant: acc.quant + Number(row.QUANT || 0),
        brl: acc.brl + Number(row.VALORBRL || 0),
        count: acc.count + 1,
      }),
      { quant: 0, brl: 0, count: 0 }
    );

  const renderSentTable = (title, rows) => {
    const totals = sentTotals(rows);
    const avgPrice = totals.quant > 0 ? totals.brl / totals.quant : 0;
    return (
      <section className={styles.tableColumn}>
        <header className={styles.tableHeader}>
          <span>{title}</span>
          <p className={styles.caption}>
            {loading ? "Carregando..." : "Registros enviados"}
          </p>
        </header>
        <div className={`${styles.tableScroll} tableScroll`}>
          <div className={styles.tableWrapper}>
            <table className={`${styles.table} ${styles.tableAuto} ${styles.noWrap}`}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Reg</th>
                  <th>Ind</th>
                  <th>Ativo</th>
                  <th>Quant (USDT)</th>
                  <th>Valor BRL</th>
                  <th>Preço USDT</th>
                  <th>País</th>
                  <th>CPF</th>
                  <th>Nome</th>
                  <th>Arquivo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const quant = Number(row.QUANT || 0);
                  const brl = Number(row.VALORBRL || 0);
                  const price = quant > 0 ? brl / quant : 0;
                  return (
                    <tr key={row.ID ?? row.id}>
                      <td>{renderCell(formatDateForDisplay(row.DATA ?? row.data))}</td>
                      <td>{renderCell(row.REG ?? row.reg ?? "—")}</td>
                      <td>{renderCell(row.IND ?? row.ind ?? "—")}</td>
                      <td>{renderCell(row.ATIVO ?? row.ativo ?? "USDT")}</td>
                      <td>{renderCell(formatUsdt8(quant))}</td>
                      <td>{renderCell(formatCurrencyBRL(brl))}</td>
                      <td>{renderCell(formatPrice4(price))}</td>
                      <td>{renderCell(row.PAIS ?? row.pais ?? "—")}</td>
                      <td>{renderCell(row.CPF ?? row.cpf ?? "-")}</td>
                      <td className={styles.nameCell} title={row.NOME || ""}>
                        {renderCell(row.NOME || "—")}
                      </td>
                      <td className={styles.nameCell} title={row.FILENAME || ""}>
                        {renderCell(row.FILENAME || "—")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.totalsBar}>
          <span className={styles.totalsItem}>Itens: {totals.count}</span>
          <span className={styles.totalsItem}>
            Total USDT: {formatUsdt8(totals.quant)}
          </span>
          <span className={styles.totalsItem}>
            Total BRL: {formatCurrencyBRL(totals.brl)}
          </span>
          <span className={styles.totalsItem}>
            Preço médio (BRL/USDT): {formatPrice4(avgPrice)}
          </span>
        </div>
      </section>
    );
  };

  const CanonTable = ({ title, rows, totals }) => (
    <section className={styles.tableColumn}>
      <header className={styles.tableHeader}>
        <span>{title}</span>
        <p className={styles.caption}>
          {loading ? "Carregando..." : "Registros atuais"}
        </p>
      </header>

      <div className={`${styles.tableScroll} tableScroll`}>
        <div className={styles.tableWrapper}>
          <table className={`${styles.table} ${styles.tableAuto} ${styles.noWrap}`}>
            <thead>
              <tr>
                {IN1888_COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id ?? row.ID ?? idx}>
                  {IN1888_COLUMNS.map((column) => {
                    const value = row[column.key] ?? "—";
                    const isName =
                      column.key === "nomeBrl" || column.key === "nomeBob";
                    return (
                      <td
                        key={column.key}
                        className={isName ? styles.nameCell : undefined}
                        style={column.align === "right" ? { textAlign: "right" } : undefined}
                      >
                        {renderCell(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
            <tr className={styles.totalsRow}>
              <td colSpan={4}>Totais</td>
              <td>{formatCurrencyBRL(totals.valorBrl)}</td>
              <td>{formatBOB(totals.valorBob)}</td>
              <td>{formatUSDTQuant(totals.usdtQuant)}</td>
              <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );

  return (
    <main className={styles.root}>
      <section className={styles.filterSection}>
        <div className={styles.filterRow}>
          <div className={styles.filterControl}>
            <label className={styles.filterLabel} htmlFor="in1888-month">
              Mês
            </label>
            <input
              id="in1888-month"
              type="month"
              value={filterMonth}
              className={styles.filterInput}
              onChange={(event) => setFilterMonth(event.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.clearFilterButton}
            onClick={() => setFilterMonth("")}
          >
            Limpar filtro
          </button>
          <p className={styles.filterStats}>
            Vendas: {filteredVendas.length} | Compras: {filteredCompras.length}
            <span className={styles.sourceBadge}>
              Fonte: {dataSource === "SENT" ? "IN1888 Enviados (TXT)" : "Compras/Vendas (Sistema)"}
            </span>
          </p>
          <div className={styles.actionsBar}>
            <button
              type="button"
              className={styles.exportButton}
              onClick={() =>
                exportFile(
                  filteredVendas,
                  buildSaleLine,
                  getFileName("vendas_IN1888", filterMonth)
                )
              }
            >
              Exportar VENDAS (IN 1888)
            </button>
            <button
              type="button"
              className={styles.exportButton}
              onClick={() =>
                exportFile(
                  filteredCompras,
                  buildCompraLine,
                  getFileName("compras_IN1888", filterMonth)
                )
              }
            >
              Exportar COMPRAS (IN 1888)
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setOpenSent(true)}
            >
              Ver TXT Enviados
            </button>
          </div>
        </div>
        {errorMessage && (
          <p className={styles.errorMessage}>{errorMessage}</p>
        )}
      </section>

      <div className={styles.tablesWrapper}>
        {dataSource === "SENT"
          ? (
            <>
              {renderSentTable("Vendas (Enviados)", sentVendas)}
              {renderSentTable("Compras (Enviados)", sentCompras)}
            </>
          )
          : (
            <>
              <CanonTable title="Vendas" rows={vendasCanon} totals={totalsVendas} />
              <CanonTable title="Compras" rows={comprasCanon} totals={totalsCompras} />
            </>
          )}
      </div>
      <IN1888EnviadosOverlay
        open={openSent}
        onClose={() => setOpenSent(false)}
        title="IN1888 — TXT Enviados"
      >
        <EnviadosModule mesRef={filterMonth} />
      </IN1888EnviadosOverlay>
      <ToastContainer limit={1} position="top-right" autoClose={3000} />
    </main>
  );
}
